import { formatEurosCents } from "@/shared/utils/price.utils";
import { DEV_MOCK_AMOUNT_CENTS } from "../configs/unpaidInvoices.config";
import { UNPAID_METHOD_DELAY } from "./unpaidPayment.utils";
import type {
  UnpaidAccessState, UnpaidInvoicesStatus, UnpaidPendingMethod, UnpaidPendingPayment,
} from "../types/unpaidInvoices.types";

export const resolveAccessState = (
  status: UnpaidInvoicesStatus | null,
  isImpersonating: boolean,
): Exclude<UnpaidAccessState, "loading"> => {
  // Blocked with nothing due is incoherent: fail open rather than lock elected members out for no reason.
  if (!status?.amountCents) return "normal";
  // A transfer or debit already on its way means the member has acted: blocking would punish a settlement in progress.
  if (status.isBlocked && !status.pendingPayment && !isImpersonating) return "blocked";
  return "warning";
};

// Blocked on purpose: the mock proves that a pending payment wins over the block.
const DEV_MOCK_PENDING_STATUS: UnpaidInvoicesStatus = {
  amountCents: DEV_MOCK_AMOUNT_CENTS,
  isBlocked: true,
  pendingPayment: { method: "bank_transfer", amountCents: DEV_MOCK_AMOUNT_CENTS },
};

/** `undefined` means "no override"; `null` forces the normal state. */
export const resolveDevMockStatus = (value: string | undefined): UnpaidInvoicesStatus | null | undefined => {
  if (value === "normal") return null;
  if (value === "warning") return { amountCents: DEV_MOCK_AMOUNT_CENTS, isBlocked: false, pendingPayment: null };
  if (value === "blocked") return { amountCents: DEV_MOCK_AMOUNT_CENTS, isBlocked: true, pendingPayment: null };
  if (value === "pending") return DEV_MOCK_PENDING_STATUS;
  if (value === "payment_method") {
    return {
      amountCents: DEV_MOCK_AMOUNT_CENTS,
      isBlocked: true,
      pendingPayment: null,
      paymentMethodRequired: true,
      staffMessage: "Le compte actuellement indiqué est clos.",
    };
  }
  return undefined;
};

type UnpaidNoticeParts = { lead: string, amount: string, tail: string };

/** Split around the amount so the banner can emphasise it while the sentence keeps a single source. */
export const unpaidNoticeParts = (amountCents: number, pendingCents = 0): UnpaidNoticeParts => {
  const isPartlyPending = pendingCents > 0 && pendingCents < amountCents;
  // The amount keeps the requested part (access stays paused until it is settled); the clause explains why the pay button shows less.
  const pendingClause = isPartlyPending ? `, dont ${formatEurosCents(pendingCents)} déjà en cours de traitement` : "";

  return {
    lead: "Sauf erreur de notre part, un règlement de ",
    amount: formatEurosCents(amountCents),
    tail: ` reste en attente sur l'abonnement Yeati de votre CSE${pendingClause}.`,
  };
};

const PENDING_METHOD_NOUN: Record<UnpaidPendingMethod, string> = {
  bank_transfer: "virement",
  sepa_debit: "prélèvement",
};

export const pendingNoticeParts = ({ method, amountCents }: UnpaidPendingPayment): UnpaidNoticeParts => ({
  lead: `Votre ${PENDING_METHOD_NOUN[method]} de `,
  amount: formatEurosCents(amountCents),
  tail: ` est en attente de confirmation. Délai habituel : ${UNPAID_METHOD_DELAY[method].toLocaleLowerCase("fr")}.`,
});

export const formatUnpaidNotice = (amountCents: number, pendingCents = 0): string => {
  const { lead, amount, tail } = unpaidNoticeParts(amountCents, pendingCents);

  return `${lead}${amount}${tail}`;
};
